import { Direction } from "odyssey-shared";

/**
 * The stick's pure mapping from a finger's offset to the eight-way intent (ADR-061's amendment of 2026-10-31,
 * decision 6).
 */

/**
 * The dead zone as a fraction of the stick's radius: a push shorter than this sends no direction.
 */
export const STICK_DEADZONE = 0.3;

/**
 * Degrees a held direction reaches into the next sector before the stick changes direction, so a thumb resting near a
 * boundary does not flutter between two directions.
 */
export const STICK_HYSTERESIS_DEGREES = 15;

const SECTOR_DEGREES = 45;

/**
 * The directions by sector, counted clockwise from Right in screen space, where y points down.
 */
const SECTORS: readonly Direction[] = Object.freeze([Direction.Right, Direction.DownRight, Direction.Down, Direction.DownLeft, Direction.Left, Direction.UpLeft, Direction.Up, Direction.UpRight]);

/**
 * The signed difference of two angles in degrees, wrapped into -180 to 180.
 *
 * @param a The first angle.
 * @param b The second angle.
 * @returns a - b wrapped.
 */
function angleBetween(a: number, b: number): number {
  const turn = (((a - b) % 360) + 540) % 360;
  return turn - 180;
}

/**
 * The direction a finger offset sends: none inside the dead zone, otherwise the 45 degree sector the offset lies in,
 * except that the direction held keeps its sector until the angle is `STICK_HYSTERESIS_DEGREES` past the sector's edge.
 *
 * @param dx The offset right of the stick's origin, in any unit.
 * @param dy The offset below the origin, in the same unit.
 * @param radius The stick's radius in the same unit.
 * @param held The direction the stick sent last.
 * @returns The direction, `Direction.None` for a dead-zone push or an offset or radius that is not a finite number
 *   with a positive radius.
 * @example
 * stickDirection(100, 0, 100, Direction.None); // Direction.Right
 */
export function stickDirection(dx: number, dy: number, radius: number, held: Direction): Direction {
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(radius) || radius <= 0) {
    return Direction.None;
  }
  if (Math.hypot(dx, dy) < radius * STICK_DEADZONE) {
    return Direction.None;
  }
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const heldSector = SECTORS.indexOf(held);
  if (heldSector >= 0 && Math.abs(angleBetween(angle, heldSector * SECTOR_DEGREES)) <= SECTOR_DEGREES / 2 + STICK_HYSTERESIS_DEGREES) {
    return held;
  }
  const sector = (((Math.round(angle / SECTOR_DEGREES) % SECTORS.length) + SECTORS.length) % SECTORS.length) as number;
  return SECTORS[sector] ?? Direction.None;
}

/**
 * Brings a point onto the circle of a radius when it lies outside, along the line from the origin: where the knob is drawn.
 *
 * @param dx The offset right of the origin.
 * @param dy The offset below the origin.
 * @param radius The radius.
 * @returns The offset, shortened to the radius if longer.
 */
export function clampToRadius(dx: number, dy: number, radius: number): { readonly x: number; readonly y: number } {
  const length = Math.hypot(dx, dy);
  if (length <= radius || length === 0) {
    return { x: dx, y: dy };
  }
  const scale = radius / length;
  return { x: dx * scale, y: dy * scale };
}
